import { useWorkflowInvitations } from "@/lib/data/react-query/connections/workflow-invitations";
import dayjs from "dayjs";
import { Copy, Mail, MailCheck, MessageSquare } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { AppModal, Badge, Button } from "@/components/ui";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveContactQueryOptions } from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type { MatterContactConnection } from "@/lib/types";
import { buildWorkflowContactConnectionPresentations } from "@/lib/utils/connections/workflow-contact-connections";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import {
  formatContactsName,
  formatDate,
  formatUsersName,
} from "@/lib/utils/format";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

type ShareFolderAccessModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  shareFolderId: string;
  title: string;
  description?: string;
};

const SHARE_FOLDER_EMAIL_TEMPLATE = "ShareFolderNotificationEmailTemplate";

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Share link copied to clipboard");
  } catch {
    toast.error("Failed to copy share link");
  }
};

const getShareFolderStatus = (expirationDate?: string) => {
  if (!expirationDate) {
    return {
      label: "Active",
      variant: "outline" as const,
    };
  }

  const isExpired = dayjs().isAfter(dayjs(expirationDate));
  return {
    label: isExpired ? "Expired" : "Active",
    variant: isExpired ? ("destructive" as const) : ("outline" as const),
  };
};

function ShareFolderAccessContactCard({
  connection,
  matterId,
  expirationDate,
  shareFolderLabel,
  onOpenText,
  onOpenEmail,
  emailSent,
}: {
  connection: MatterContactConnection;
  matterId: string;
  expirationDate?: string;
  shareFolderLabel: string;
  onOpenText: (connection: MatterContactConnection) => void;
  onOpenEmail: (connection: MatterContactConnection) => void;
  emailSent: boolean;
}) {
  const passcode = connection.shareFolderPasscode;
  const shareLink = passcode
    ? `${window.location.origin}/file-share?passcode=${passcode}`
    : "";
  const primaryEmail = connection.contact.emails[0]?.address;
  const primaryPhone = connection.contact.phones[0]?.number;
  const status = getShareFolderStatus(expirationDate);
  const contactId = connection.contactId;

  return (
    <div className="space-y-3">
      <MatterContactConnectionModalPreview
        contactId={contactId}
        previewContact={connection.contact}
        matterId={matterId}
        matterContactConnection={connection}
        visualVariant="white"
      />
      <div className="space-y-3 rounded-md border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant={status.variant}
            className={status.label === "Expired" ? "uppercase" : undefined}
          >
            {status.label}
          </Badge>
          {expirationDate ? (
            <span className="text-muted-foreground text-xs">
              {status.label === "Expired" ? "Expired" : "Expires"}{" "}
              {formatDate(convertToLocalFromDatabaseDateTime(expirationDate))}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="copy-share-link-button"
            type="button"
            variant="outline"
            size="sm"
            disabled={!shareLink}
            onClick={() => {
              if (shareLink) {
                void copyToClipboard(shareLink);
              }
            }}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" />
            Copy link
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!primaryPhone || !shareLink}
            onClick={() => {
              onOpenText(connection);
            }}
          >
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
            Text
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!primaryEmail}
            onClick={() => {
              onOpenEmail(connection);
            }}
          >
            {emailSent ? (
              <MailCheck className="mr-1.5 h-3.5 w-3.5 text-green-500" />
            ) : (
              <Mail className="mr-1.5 h-3.5 w-3.5" />
            )}
            Email
          </Button>
        </div>
        {passcode ? (
          <p className="text-muted-foreground text-xs">
            Passcode for {shareFolderLabel}: {passcode}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export const ShareFolderAccessModal = ({
  open,
  setOpen,
  matterId,
  shareFolderId,
  title,
  description,
}: ShareFolderAccessModalProps) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const { openCommunicationModal, openSendEmailModal } = useGlobalModal();
  const { currentUser } = useUser();
  const queryClient = useQueryClient();

  const shareFolder = useMemo(() => {
    return matterData.data.shareFolders.find(
      (folder) => folder.id === shareFolderId,
    );
  }, [matterData.data.shareFolders, shareFolderId]);

  const { data: invitations = [] } = useWorkflowInvitations(
    `matters/v4/share-folders/${shareFolder?.id ?? ""}`,
    matterId,
    Boolean(shareFolder),
  );

  const contacts = useMemo(
    () =>
      shareFolder
        ? buildWorkflowContactConnectionPresentations({
            invitations,
            connections: shareFolder.connections,
            label: "Accessor",
            matterId,
            sourceObjectType: "matter_share_folder",
          })
        : [],
    [matterId, shareFolder, invitations],
  );

  const [emailSent, setEmailSent] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) {
      setEmailSent({});
    }
  }, [open]);

  const handleOpenText = (connection: MatterContactConnection) => {
    const contactId = connection.contactId;
    const passcode = connection.shareFolderPasscode;

    if (!contactId) {
      toast.error("Contact information is missing.");
      return;
    }

    if (!passcode) {
      toast.error("No passcode available for this contact.");
      return;
    }

    void queryClient.prefetchQuery(retrieveContactQueryOptions(contactId));

    const shareLink = `${window.location.origin}/file-share?passcode=${passcode}`;
    const folderLabel = shareFolder?.label ?? "shared folder";

    openCommunicationModal({
      contactId,
      matterId,
      defaultMessage: `You have access to ${folderLabel}. Use this secure link to view the shared files: ${shareLink}\nPasscode: ${passcode}`,
    });
  };

  const handleOpenEmail = (connection: MatterContactConnection) => {
    const contactId = connection.contactId;
    const passcode = connection.shareFolderPasscode;

    if (!contactId) {
      toast.error("Contact information is missing.");
      return;
    }

    if (!passcode) {
      toast.error("No passcode available for this contact.");
      return;
    }

    if (emailSent[contactId]) {
      toast.success("Email recently sent to this contact.");
      return;
    }

    const primaryEmail = connection.contact.emails[0]?.address;

    if (!primaryEmail) {
      toast.error("Contact does not have an email address.");
      return;
    }

    const shareLink = `${window.location.origin}/file-share?passcode=${passcode}`;
    const folderLabel = shareFolder?.label ?? "Shared Folder";
    const expirationDate = shareFolder?.expirationDate ?? "";
    const senderName = formatUsersName(currentUser) || "";
    const firmName = currentUser.firm.name;

    const templateData = {
      share_folder_label: folderLabel,
      share_folder_passcode: passcode,
      share_folder_link: shareLink,
      expiration_date: expirationDate,
      sender_name: senderName,
      firm_name: firmName,
      recipient_name: formatContactsName(connection.contact) ?? "",
      subject: "Share Folder Access",
      preheader: "You have access to a shared folder.",
    };

    openSendEmailModal({
      title: "Send share folder access email",
      description: "Review and send the share folder access email.",
      toEmails: [primaryEmail],
      contactIds: [contactId],
      templateName: SHARE_FOLDER_EMAIL_TEMPLATE,
      templateData,
      onSuccess: () => {
        setEmailSent((prev) => ({ ...prev, [contactId]: true }));
      },
    });
  };

  const expirationDate = shareFolder?.expirationDate;
  const shareFolderLabel = shareFolder?.label ?? "Shared Folder";

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={description}
      size="3xl"
      footer={
        <div className="flex w-full justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {contacts.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No contacts have been added to this share folder.
          </p>
        ) : (
          contacts.map((connection) => (
            <ShareFolderAccessContactCard
              key={connection.id}
              connection={connection}
              matterId={matterId}
              expirationDate={expirationDate}
              shareFolderLabel={shareFolderLabel}
              onOpenText={handleOpenText}
              onOpenEmail={handleOpenEmail}
              emailSent={Boolean(emailSent[connection.contactId])}
            />
          ))
        )}
      </div>
    </AppModal>
  );
};

ShareFolderAccessModal.displayName = "ShareFolderAccessModal";
